// API key DTO — what the dashboard displays and the SDK references. The actual
// secret is returned exactly once, at creation time (CreateApiKeyResult), and
// never again: storage is HMAC of the secret, not the secret itself.
import { Type } from '@sinclair/typebox';
export const API_KEY_SCOPES = [
    'chat:read',
    'chat:write',
    'agents:read',
    'agents:write',
    // Phase 4.8 — gates GET /v1/usage, POST /v1/billing/portal,
    // GET /v1/billing/invoices. Issued only to dashboard-side keys.
    'billing:read',
    // Phase 5.2 — gates the /v1/api-keys CRUD surface. Granted to the dashboard
    // JWT via CLERK_USER_SCOPES; an existing API key can only mint or revoke
    // sibling keys when it was itself issued with these scopes.
    'api_keys:read',
    'api_keys:write',
    // Phase 3.x widget surface — required to call POST /v1/widget/session
    // from a tenant's server. The widget itself never carries an API key;
    // it uses the short-lived session token returned by the mint call.
    'widget:mint',
    // Phase 4.x audit surface — gates GET /v1/audit-log. Read-only.
    'audit:read',
    'admin',
];
export const ApiKeyScopeSchema = Type.Union(API_KEY_SCOPES.map((s) => Type.Literal(s)), { $id: 'ApiKeyScope' });
// Displayed shape. `prefix` is the `ck_live_<prefix8>` segment — safe to show.
// `last4` is the last 4 chars of the secret segment, for recognition.
export const ApiKeySchema = Type.Object({
    id: Type.String({ format: 'uuid' }),
    tenantId: Type.String({ format: 'uuid' }),
    name: Type.String({ minLength: 1, maxLength: 128 }),
    prefix: Type.String({ minLength: 12, maxLength: 20 }),
    last4: Type.String({ minLength: 4, maxLength: 4 }),
    scopes: Type.Array(ApiKeyScopeSchema, { minItems: 1, maxItems: 8 }),
    createdAt: Type.String({ format: 'date-time' }),
    lastUsedAt: Type.Optional(Type.Union([Type.String({ format: 'date-time' }), Type.Null()])),
    revokedAt: Type.Optional(Type.Union([Type.String({ format: 'date-time' }), Type.Null()])),
    expiresAt: Type.Optional(Type.Union([Type.String({ format: 'date-time' }), Type.Null()])),
}, { $id: 'ApiKey', additionalProperties: false });
export const CreateApiKeyInputSchema = Type.Object({
    name: Type.String({ minLength: 1, maxLength: 128 }),
    scopes: Type.Array(ApiKeyScopeSchema, { minItems: 1, maxItems: 8 }),
    expiresAt: Type.Optional(Type.String({ format: 'date-time' })),
}, { $id: 'CreateApiKeyInput', additionalProperties: false });
// Returned exactly once. `secret` is the full `ck_live_...` string. After the
// client dismisses this response, the platform cannot recover it.
export const CreateApiKeyResultSchema = Type.Object({
    key: ApiKeySchema,
    secret: Type.String({ minLength: 20, maxLength: 128 }),
}, { $id: 'CreateApiKeyResult', additionalProperties: false });
//# sourceMappingURL=api-keys.js.map